import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { type ApiResult } from '@app/client';
import { callGetFloorPriceChanges } from '@app/client/resources/marketplace';
import { type FloorPriceChangesProps, type FloorPriceChangesResponse } from '@app/client/resources/marketplace/types';
import { PathParams } from '@app/routes/types';

export const FLOOR_PRICE_CHANGES_CACHE = 'floor_price_changes_cache';

type UseGetFloorPriceChangesDataProps = {
    slug?: string;
    floorPriceChanges: FloorPriceChangesProps;
};
export const useGetFloorPriceChangesData = ({
    floorPriceChanges,
    slug,
    disabled,
}: UseGetFloorPriceChangesDataProps & { disabled?: boolean }) => {
    const { [PathParams.WishlistSlug]: wishlistSlug } = useParams();

    const cache = [
        FLOOR_PRICE_CHANGES_CACHE,
        wishlistSlug || slug,
        ...(floorPriceChanges.slugs || []),
        ...(floorPriceChanges.contracts || []),
    ];

    const response = useQuery(
        cache,
        ({ signal }) =>
            callGetFloorPriceChanges({
                ...floorPriceChanges,
                settings: {
                    signal,
                },
            }),
        {
            retry: false,
            keepPreviousData: true,
            select: (res) => {
                const data = res as ApiResult<FloorPriceChangesResponse>;
                if (data?.ok && data.value) {
                    return data?.value;
                }
            },
            enabled: Boolean(wishlistSlug || slug) && !disabled,
        },
    );

    const pricesPerSlug =
        response.data?.reduce<{
            [slug: string]: { quantity: number; currentPrice: number; pastPrice: number };
        }>((acc, curr) => {
            if (!acc[curr.slug]) {
                acc[curr.slug] = {
                    quantity: 0,
                    currentPrice: parseFloat(curr.currentPrice),
                    pastPrice: parseFloat(curr.previousPrices[0].price),
                };
            }
            acc[curr.slug].quantity += 1;
            return acc;
        }, {}) || {};

    const totalFloorPriceChanges = Object.keys(pricesPerSlug).reduce<{
        current: number;
        past: number;
        slugs: string[];
    }>(
        (acc, slug) => {
            if (!acc) acc = { current: 0, past: 0, slugs: [] };
            const floorPriceData = pricesPerSlug[slug];
            let quantity = floorPriceData.quantity;
            if (floorPriceChanges.slugs?.includes(slug) && quantity > 1) {
                // Single collection counts as a token
                // if collection and tokens collection gets ignored
                quantity -= 1;
            }
            acc.current += floorPriceData.currentPrice * quantity;
            acc.past += floorPriceData.pastPrice * quantity;
            return acc;
        },
        { current: 0, past: 0, slugs: [] },
    );

    return { ...response, totalFloorPriceChanges };
};
